import React, {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  IconButton,
  InputBase,
  Stack,
} from '@mui/material';
import api from '../../../services/api';
import Box from '@mui/material/Box';
import {
  DataGridPro,
  GridColDef,
  GridFooter, GridToolbar,
} from '@mui/x-data-grid-pro';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import {datagridColors} from '../../../assets/datagridStyles';
import {useGridApiRef} from '@mui/x-data-grid';
import {Header} from '../Header';
import {styled} from '@mui/material/styles';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import {LockOpenRounded} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import Tooltip from "@mui/material/Tooltip";
import {LoadingButton} from "@mui/lab";

const Search = styled('div')(({theme}) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'rgb(0 0 0 / 5%)',
  '&:hover': {
    backgroundColor: 'rgb(0 0 0 / 7%)',
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const StyledInputBase = styled(InputBase)(({theme}) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));
const SearchIconWrapper = styled('div')(({theme}) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const LockManagement: ComponentType = () => {
  const [loading, setLoading] = useState(false);
  const [loadingKickOutButton, setLoadingKickOutButton] = useState(false);
  const [rows, setRows] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(undefined);
  const apiRef = useGridApiRef();
  const [fastSearchValue, setFastSearchValue] = useState<any>('');

  const fetchLocks = useCallback(() => {
    setLoading(true);

    const searchParams = new URLSearchParams();
    searchParams.set('searchValue', fastSearchValue);

    api.get(`/locks`, searchParams).then((res: any) => {
      setRows(res);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    document.title = `Zoe - Lock Management`;
    fetchLocks();
  }, []);

  const handleUnlockClick = (row: any) => {
    setOpenConfirmDialog(row);
  };

  const handleKickOut = (row: any) => {
    setLoadingKickOutButton(true);

    api.post(`/kick/${row.id}`,{
      table: row.table
    }).then(()=>{

      setRows((prevRows: any) => prevRows.filter((r:any) => r.id !== row.id));

    }).catch(()=>{

    }).finally(()=>{
      setLoadingKickOutButton(false);
      setOpenConfirmDialog(false);
    });

  };

  const columns: GridColDef[] = useMemo(() => {
    const columnArray: GridColDef[] = [
      {
        field: 'user',
        headerName: 'Editing by',
        editable: false,
        type: 'string',
        minWidth: 125,
        flex: 1,
      },
      {
        field: 'table',
        headerName: 'Table',
        editable: false,
        type: 'string',
        minWidth: 125,
        flex: 1,
      },
      {
        field: 'row_name',
        headerName: 'Editing row',
        editable: false,
        type: 'string',
        minWidth: 125,
        flex: 1,
      },
      {
        field: 'relation_id',
        headerName: 'Editing row ID',
        editable: false,
        type: 'string',
        minWidth: 125,
        flex: 1,
      },
      {
        field: 'locked_at',
        headerName: 'Date',
        editable: false,
        type: 'dateTime',
        minWidth: 50,
        flex: 1,
        valueGetter: (props: any) => {
          if (props?.value !== null && props?.value !== undefined) {
            const dateValue = new Date(props.value);
            return dateValue;
          } else {
            return null;
          }
        }
      },
      {
        field: 'actions',
        type: 'string',
        headerName: '',
        cellClassName: '',
        editable: false,
        width: 100,
        renderCell: (props: any) => {
          const id = props.id;

          return (
            <>
              <Stack direction={'row'}>
                <Tooltip title={'Kick user'}>
                  <IconButton>
                    <LockOpenRounded
                      color={'primary'}
                      onClick={() => {
                        handleUnlockClick(props.row);
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
            </>
          );
        },
        renderHeaderFilter: () => {
          return <></>;
        },
      },
    ];
    return columnArray;
  }, [rows]);

  const CustomFooter = () => {
    return (
      <Stack
        direction={'row'}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 8px',
        }}
      >
        <IconButton
          sx={{
            visibility: 'hidden'
          }}
          onClick={() => {
          }}
        >
          <AddIcon color={'primary'}/>
        </IconButton>
        <GridFooter/>
      </Stack>
    );
  };

  function CustomSortIconDesc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropDownRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIconAsc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIcon(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  return (
    <>
      <Header
        title={'Lock Management'}
        rowCount={rows.length}
        hasPagination={false}
      />

      <Box sx={datagridColors('calc(100vh - 145px)')}>
        <DataGridPro
          apiRef={apiRef}
          sx={{
            pointerEvents: loading ? 'none' : 'auto',
            opacity: loading ? 0.5 : 1,
            maxHeight: 'calc(100% - 70px)',
          }}
          rows={rows}
          getRowClassName={(params: any) => {
            const color =
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
            return `${color}`;
          }}
          columns={columns}
          loading={loading}
          columnHeaderHeight={38}
          rowHeight={38}
          pagination
          slots={{
            // @ts-ignore
            columnSortedDescendingIcon: CustomSortIconDesc,
            columnSortedAscendingIcon: CustomSortIconAsc,
            columnUnsortedIcon: CustomSortIcon,
            footer: CustomFooter,
            toolbar: GridToolbar
          }}
          unstable_headerFilters={true}
          pageSizeOptions={[25, 50, 100, {label: 'All', value: rows.length}]}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </Box>

      {openConfirmDialog && (
        <>
          <Dialog
            open={true}
            onClose={() => {
              setOpenConfirmDialog(undefined);
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              Are you sure you want to kick out {openConfirmDialog.user} from editing?
            </DialogTitle>
            <DialogContent
              sx={{
                fontSize: {
                  xs: '14px',
                },
              }}
            >

            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenConfirmDialog(undefined);
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                loading={loadingKickOutButton}
                variant={'contained'}
                color={'error'}
                onClick={() => {
                 handleKickOut(openConfirmDialog);
                }}
                autoFocus
              >
                Kick out
              </LoadingButton>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};
